import { useEffect, useState } from 'react';
import { Box, TablePagination, TextField, Typography } from '@mui/material';

interface TablePaginationProps {
  recordLabel?: string;
  count: number;
  pageIndex: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

/** Page numbers shown to users start at 1; the table/API use a zero-based index. */
export default function TablePaginationControls({ recordLabel = 'users', count, pageIndex, pageSize, onPageChange, onPageSizeChange }: TablePaginationProps) {
  const pageCount = Math.max(1, Math.ceil(count / pageSize));
  const [pageInput, setPageInput] = useState(String(pageIndex + 1));
  const [invalidPage, setInvalidPage] = useState(false);

  useEffect(() => {
    setPageInput(String(pageIndex + 1));
    setInvalidPage(false);
  }, [pageIndex, pageCount]);

  const jumpToPage = () => {
    const page = Number(pageInput);
    if (!/^[1-9]\d*$/.test(pageInput) || !Number.isSafeInteger(page) || page > pageCount) {
      setInvalidPage(true);
      return;
    }
    setInvalidPage(false);
    if (page !== pageIndex + 1) onPageChange(page - 1);
  };

  return <Box sx={{
    display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1,
    flexShrink: 0, backgroundColor: '#fff', color: '#172b29', borderTop: '1px solid #d5dedc', p: 1,
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <TextField label="Page" type="number" size="small" value={pageInput} disabled={pageCount === 1}
        onChange={event => { setPageInput(event.target.value); setInvalidPage(false); }}
        onBlur={jumpToPage}
        onKeyDown={event => { if (event.key === 'Enter') (event.target as HTMLInputElement).blur(); }}
        error={invalidPage} helperText={invalidPage ? `Enter 1–${pageCount}` : undefined}
        slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: 1, max: pageCount, step: 1, 'aria-label': 'Go to page' } }}
        sx={{ width: 100, '& .MuiInputBase-input, & .MuiInputLabel-root': { color: '#172b29' },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d5dedc', backgroundColor: 'transparent' } }} />
      <Typography variant="body2" color="inherit">of {pageCount}</Typography>
    </Box>
    <TablePagination component="div" count={count} page={pageIndex} rowsPerPage={pageSize}
      rowsPerPageOptions={[25, 50, 100, 250, 500, 1000]} showFirstButton showLastButton
      onPageChange={(_, page) => onPageChange(page)}
      onRowsPerPageChange={event => onPageSizeChange(Number(event.target.value))}
      labelDisplayedRows={({ from, to, count: total }) => total === 0 ? `0 ${recordLabel}` : `${from}–${to} of ${total} ${recordLabel}`}
      sx={{ color: 'inherit', overflow: 'visible',
        '& .MuiTablePagination-toolbar': { minHeight: 48, flexWrap: 'wrap', justifyContent: 'flex-end', pl: 0 },
        '& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel, & .MuiSelect-select': { color: '#172b29' },
        '& .MuiIconButton-root:not(.Mui-disabled)': { color: '#374b48' },
        '& .MuiTablePagination-spacer': { display: 'none' },
      }} />
  </Box>;
}
